/**
 * Euler trail utilities.
 *
 * Theorem (Euler, 1736): a connected undirected graph has an Euler trail iff
 * it has exactly 0 or 2 odd-degree vertices.
 *
 * `adjustForEulerTrail` pairs odd-degree vertices by adding edges between them
 * until at most 2 remain — guaranteeing existence per GAME-SPEC §1.2.
 *
 * `findEulerTrail` (Hierholzer) returns one valid path (sequence of edge ids).
 */

import { pickInt, shuffle } from './rng';
import type { Edge } from './types';

export function degrees(edges: Edge[], vertexCount: number): number[] {
  const d = new Array<number>(vertexCount).fill(0);
  for (const e of edges) {
    d[e.a]! += 1;
    d[e.b]! += 1;
  }
  return d;
}

export function oddDegreeVertices(edges: Edge[], vertexCount: number): number[] {
  const d = degrees(edges, vertexCount);
  const out: number[] = [];
  for (let i = 0; i < vertexCount; i++) if ((d[i]! & 1) === 1) out.push(i);
  return out;
}

export function adjustForEulerTrail(edges: Edge[], vertexCount: number, rng: () => number): Edge[] {
  const work = edges.slice();
  let nextId = work.reduce((m, e) => Math.max(m, e.id), -1) + 1;
  let odds = oddDegreeVertices(work, vertexCount);

  while (odds.length > 2) {
    const shuffled = shuffle(rng, odds);
    const a = shuffled[0]!;
    const b = shuffled[1]!;
    work.push({ id: nextId++, a, b });
    odds = oddDegreeVertices(work, vertexCount);
  }
  return work;
}

/**
 * isConnected — BFS treating only non-isolated vertices as required.
 */
export function isConnected(edges: Edge[], vertexCount: number): boolean {
  if (vertexCount === 0) return true;
  const adj = new Map<number, number[]>();
  for (const e of edges) {
    if (!adj.has(e.a)) adj.set(e.a, []);
    if (!adj.has(e.b)) adj.set(e.b, []);
    adj.get(e.a)!.push(e.b);
    adj.get(e.b)!.push(e.a);
  }
  const seen = new Set<number>();
  const start = edges[0]?.a ?? 0;
  const stack = [start];
  while (stack.length) {
    const v = stack.pop()!;
    if (seen.has(v)) continue;
    seen.add(v);
    for (const u of adj.get(v) ?? []) if (!seen.has(u)) stack.push(u);
  }
  // every vertex appearing in any edge must be in seen
  for (const e of edges) {
    if (!seen.has(e.a) || !seen.has(e.b)) return false;
  }
  return true;
}

/**
 * findEulerTrail — Hierholzer's algorithm.
 * Returns edge id sequence, or null if none exists.
 */
export function findEulerTrail(edges: Edge[], vertexCount: number): number[] | null {
  if (!isConnected(edges, vertexCount)) return null;
  const odds = oddDegreeVertices(edges, vertexCount);
  if (odds.length !== 0 && odds.length !== 2) return null;
  const start = odds.length === 2 ? odds[0]! : (edges[0]?.a ?? 0);

  // Multigraph-aware adjacency: vertex → list of (neighbor, edgeId)
  const adj = new Map<number, Array<{ v: number; e: number }>>();
  for (const e of edges) {
    if (!adj.has(e.a)) adj.set(e.a, []);
    if (!adj.has(e.b)) adj.set(e.b, []);
    adj.get(e.a)!.push({ v: e.b, e: e.id });
    adj.get(e.b)!.push({ v: e.a, e: e.id });
  }
  // Hierholzer's algorithm. The valid Euler trail is the order edges are popped
  // off the stack on BACKTRACK, then reversed — NOT the order they're pushed.
  // (Push order is not a connected walk whenever the search backtracks, which
  // would make the solution replay jump between disconnected segments.)
  const used = new Set<number>();
  const stack: number[] = [start];
  const edgeStack: number[] = []; // edge by which we arrived at each pushed vertex
  const edgeTrail: number[] = []; // edges in reverse trail order
  while (stack.length) {
    const v = stack[stack.length - 1]!;
    const neighbors = adj.get(v) ?? [];
    let advanced = false;
    while (neighbors.length) {
      const next = neighbors.pop()!;
      if (used.has(next.e)) continue;
      used.add(next.e);
      stack.push(next.v);
      edgeStack.push(next.e);
      advanced = true;
      break;
    }
    if (!advanced) {
      stack.pop();
      const arrivingEdge = edgeStack.pop();
      if (arrivingEdge !== undefined) edgeTrail.push(arrivingEdge);
    }
  }
  edgeTrail.reverse();
  return used.size === edges.length ? edgeTrail : null;
}

/**
 * Test helper — generate a small canonical graph that always has a trail.
 */
export function squareGraph(): Edge[] {
  return [
    { id: 0, a: 0, b: 1 },
    { id: 1, a: 1, b: 2 },
    { id: 2, a: 2, b: 3 },
    { id: 3, a: 3, b: 0 },
  ];
}

export function randomVertexPair(vertexCount: number, rng: () => number): [number, number] {
  const a = pickInt(rng, 0, vertexCount - 1);
  let b = pickInt(rng, 0, vertexCount - 1);
  while (b === a) b = pickInt(rng, 0, vertexCount - 1);
  return [a, b];
}
